import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useFig } from '../../session/FigProvider.js';
import type { PortalTermsState } from '../../core/api/types.js';
import { ErrorState } from '../../components/States.js';
import { FullPageSpinner } from '../../components/FullPageSpinner.js';
import { PORTAL_TERMS } from './termsText.js';

/**
 * Terms acceptance, shown once, before the portal.
 *
 * The whole document is on screen above the button — it is under 200 words and does not
 * scroll on a phone — which is why the button says **"I agree to this"** rather than "I have
 * read and accept". Asking someone to assert they have read something they cannot see is an
 * induced lie, and removing it was the point of writing these terms this way.
 *
 * `acceptanceRequired` is read from the API rather than compared here: the server owns which
 * version is current, and echoing back a version we were not shown is a 409 by design.
 */
export function TermsGate({ children }: { children: ReactNode }) {
  const { client } = useFig();
  const [state, setState] = useState<PortalTermsState | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try { setState(await client.getPortalTerms()); } catch (e) { setError(e); }
  }, [client]);

  useEffect(() => { void load(); }, [load]);

  if (error !== null) {
    return <main className="page"><ErrorState error={error} onRetry={() => void load()} /></main>;
  }
  if (state === null) return <FullPageSpinner label="Loading" />;
  if (!state.acceptanceRequired) return <>{children}</>;

  async function accept() {
    if (!state) return;
    setSubmitting(true);
    try {
      // Echo back the version actually displayed. The text rendered below and
      // `state.currentVersion` must agree — see termsText.ts.
      await client.acceptPortalTerms(state.currentVersion);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="page page-terms">
      <h1 className="page-title">{PORTAL_TERMS.title}</h1>
      <div className="terms">
        {PORTAL_TERMS.sections.map((s) => (
          <section key={s.heading}>
            <h2 className="terms-heading">{s.heading}</h2>
            <ul>{s.points.map((p) => <li key={p}>{p}</li>)}</ul>
          </section>
        ))}
        <p className="state-body">{PORTAL_TERMS.closing}</p>
      </div>
      <button className="btn btn-primary" onClick={() => void accept()} disabled={submitting}>
        {submitting ? 'Saving…' : 'I agree to this'}
      </button>
    </main>
  );
}
